import React from "react";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import {
  Badge,
  BarChartIcon,
  Breadcrumbs,
  ButtonGroup,
  Card,
  ClockIcon,
  CopyableItem,
  ExternalLinkIcon,
  FileTextIcon,
  Grid,
  InfoGroup,
  InfoRow,
  ListIcon,
  LoadingState,
  PackageIcon,
  PageHeader,
  Section,
  StatsCard,
  SubHeadline,
} from "@KristianIV/common-ui";
import PageLayout from "../shell/PageLayout";
import RouteButton, { ExternalButton } from "../shell/RouteButton";
import { ApplicationState } from "../reducers";
import { ONE_HOUR_AGO } from "./firestore/triangulations";
import { countDevices } from "./deviceRows";
import DevicesTable from "./DevicesTable";
import OperatingMode from "./OperatingMode";
import { resolveOfflineThresholds } from "./scoutState";
import { useOrganisationListeners } from "./useOrganisationListeners";

export const firestoreOrganisationUrl = (organisationId: string) =>
  `https://console.firebase.google.com/project/triangula-385d2/firestore/databases/-default-/data/~2Fenv~2Fprod~2Fapp~2Fcommander~2Forganisations~2F${organisationId}`;

const OrganisationPage = ({ match }: RouteComponentProps<{ id: string }>) => {
  const organisationId = match.params.id;
  const history = useHistory();
  // The dashboard's user search links here with the searched user's name.
  const location = useLocation<{ username?: string } | undefined>();
  const username = location.state?.username;

  const jwtToken = useSelector((state: ApplicationState) => state.login.jwtToken);
  const organisationInfo = useSelector((state: ApplicationState) => state.organisation.organisationInfo);
  const scouts = useSelector((state: ApplicationState) => state.organisation.scouts);
  const observations = useSelector((state: ApplicationState) => state.organisation.observations);
  const gunshots = useSelector((state: ApplicationState) => state.organisation.gunshots);
  const fastTrackScouts = useSelector((state: ApplicationState) => state.organisation.fastTrackScouts);

  useOrganisationListeners(organisationId, jwtToken);

  const scoutList = Object.values(scouts);
  const counts = countDevices(scoutList, resolveOfflineThresholds(organisationInfo?.offline, false));
  const shotsLastHour = gunshots.filter((g) => g.timestamp.getTime() > Date.now() - ONE_HOUR_AGO).length;
  const eventsLastMinute = Object.values(observations).reduce((sum, list) => sum + list.length, 0);

  return (
    <PageLayout>
      <Breadcrumbs
        items={[]}
        onHomeClick={() => history.push("/")}
        currentLabel={organisationInfo ? organisationInfo.name : "Organisation"}
      />

      {!organisationInfo ? (
        <LoadingState message="Loading organisation..." />
      ) : (
        <>
          <PageHeader
            title={organisationInfo.name}
            actions={
              <ButtonGroup aria-label="Organisation pages">
                <RouteButton to={`/incidents?organisation-id=${organisationId}`} icon={<ListIcon />}>
                  Event log
                </RouteButton>
                <RouteButton to={`/logs?organisation-id=${organisationId}`} icon={<FileTextIcon />}>
                  Logs
                </RouteButton>
                <RouteButton to={`/analytics/organisation/${organisationId}/metrics`} icon={<BarChartIcon />}>
                  Metrics
                </RouteButton>
                <RouteButton to={`/analytics/organisation/${organisationId}/clock`} icon={<ClockIcon />}>
                  Clock analytics
                </RouteButton>
                <RouteButton to={`/scoutVersions?organisation-id=${organisationId}`} icon={<PackageIcon />}>
                  App versions
                </RouteButton>
                <ExternalButton href={firestoreOrganisationUrl(organisationId)} icon={<ExternalLinkIcon />}>
                  Firestore
                </ExternalButton>
              </ButtonGroup>
            }
          >
            <Badge size="big">{organisationInfo.mode} mode</Badge>
          </PageHeader>

          <Section>
              <Grid>
                <StatsCard title="Shots last hour" value={String(shotsLastHour)} interactive={false} />
                <StatsCard
                  title="Listening devices"
                  value={String(counts.listening)}
                  footerItems={[
                    { label: `${counts.synchronizing} synchronizing · ${counts.muted} muted · ${counts.offline} offline`, value: "" },
                  ]}
                  interactive={false}
                />
                <StatsCard title="Events last minute" value={String(eventsLastMinute)} interactive={false} />
              </Grid>
          </Section>

          <Section>
              <Grid>
                <Card interactive={false} padded>
                  <SubHeadline>Organisation</SubHeadline>
                  <InfoGroup>
                    <InfoRow label="Access code">
                      <CopyableItem value={organisationInfo.accessCode} label="access code" />
                    </InfoRow>
                    <InfoRow label="Organisation ID">
                      <CopyableItem value={organisationId} label="organisation ID" />
                    </InfoRow>
                    <InfoRow label="Units">{organisationInfo.units}</InfoRow>
                    {organisationInfo.sales && <InfoRow label="Account">Sales account</InfoRow>}
                    {username && <InfoRow label="User">{username}</InfoRow>}
                  </InfoGroup>
                </Card>
                <OperatingMode organisationInfo={organisationInfo} />
              </Grid>
          </Section>

          <Section>
              <DevicesTable
                organisationId={organisationId}
                scouts={scoutList}
                observations={observations}
                fastTrackScouts={fastTrackScouts}
                offline={organisationInfo.offline}
              />
          </Section>
        </>
      )}
    </PageLayout>
  );
};

export default OrganisationPage;
